<template>
    <div class="sms-template">
        <h1 class="text-left">Edit an SMS Template</h1>

        <ValidationObserver ref="smsTemplateForm" tag="form" v-on:submit.prevent="saveContent()">
            <div>
                <ul>
                    <li>
                        <label for="content">Message <variables v-bind:variables="variablesCopy" v-bind:obj="smsTemplate" v-bind:propertyName="'content'" v-bind:inputTextArea="contentInput" v-bind:popupPosition="'bottom-right'"></variables></label>
                        <div>
                            <ValidationProvider rules="required|min:20" v-slot="{ errors }">
                                <textarea v-model="smsTemplate.content" 
                                          @keydown="changed = true" 
                                          @input="sanitizeContent"
                                          id="content" 
                                          ref="content" 
                                          type="text" 
                                          class="sms-template__content form-account__text long-question"></textarea>
                                <div class="sms-template__chars-remaining">
                                    <span class="sms-template__chars-remaining-count" v-bind:class="{'sms-template__chars-remaining-count--negative' : remainingCharacters < 0}">{{remainingCharacters}}</span> characters left
                                </div>
                                <span class="form__error" v-if="errors">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                    </li>

                    <li>
                        <div class="sms-template__preview">
                            <label for="preview">Preview</label>
                            <div class="sms-template__preview-note">
                                <span class="">Variables in your message are replaced with sample data and highlighted in</span> <span class='sms-template__preview-variable'>blue</span>
                            </div>
                            <div id="preview" class="sms-template__preview-phone">
                                <div class="sms-template__preview-bubble" v-html="previewContentHTML"></div>
                            </div>
                        </div>
                    </li>
                </ul>


                <div v-if="updating || error" class="infobar" v-bind:class="{'infobar-info' : updating, 'infobar-error' : error}">
                    {{infoMessage}}
                </div>

                <div class="row" v-show="!updating">
                    <div class="col-6 offset-3">
                        <div class="row">
                            <div class="col-6">
                                <button class="btn btn-secondary" v-on:click.prevent="cancel">Cancel</button>
                            </div>
                            <div class="col-6">
                                <button class="btn btn-primary" v-bind:aria-disabled="!changed" v-bind:class="{'btn-disabled' : !changed}">Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ValidationObserver>

        <confirm-dialog v-bind:continuePrimaryText="'Save'"
                        v-bind:message="confirmLoseChangesMessage"
                        v-bind:callbackPrimaryName="'save-sms-template'"
                        v-bind:callbackPrimaryReference="'save'"
                        v-if="showConfirmLoseChangesDialog"/>
    </div>
</template>

<script>
    import { ValidationObserver, ValidationProvider, /*extend*/ } from 'vee-validate'

    import { store, getters } from '../store.js'
    import Variables from "../components/establishments/Variables";
    import ConfirmDialog from '../components/ConfirmDialog.vue'

    import { variablesMixin } from '../mixins/VariablesMixin'

    export default {
        name: 'EditSMSTemplate',
        components: {
            ValidationObserver, ValidationProvider, Variables, ConfirmDialog
        },
        mixins: [ variablesMixin ],
        data() {
            return {
                reference: null,
                contentInput: null,
                smsTemplate: {
                    content: "",
                },
                requiredVariables: [],
                changed: false,
                cancelling: false,
                updating: false,
                error: false,
                infoMessage: "",
                confirmLoseChangesMessage: "You have un-saved changes to this SMS template. Would you like to save your work before leaving the page?",
                showConfirmLoseChangesDialog: false,
                navigatingToRoute: "",
                parentAutomation: null
            }
        },
        computed: {
            getUnitTypeForDisplay: function() {
                return getters.getCurrentEstablishment().preferences.displayCategoryForRoomTypes;
            },
            remainingCharacters: function() {
                console.log(this.previewContent.length);
                return 160 - this.previewContent.length;
            },
            previewContent: function() {
                let previewText = this.smsTemplate.content;
                let variables = previewText.matchAll(/\${(.+?)}/g);
                if (variables) {
                    for (let variable of variables) {
                        let value = this.getExampleForVariable(variable[1]);
                        previewText = previewText.replaceAll(variable[0], value);
                    }
                }
                return previewText;
            },
            previewContentHTML: function() {
                let previewText = this.smsTemplate.content;
                let variables = previewText.matchAll(/\${(.+?)}/g);
                if (variables) {
                    for (let variable of variables) {
                        let value = this.getExampleForVariable(variable[1]);
                        previewText = previewText.replaceAll(variable[0], "<span class='sms-template__preview-variable'>" + value + "</span>");
                    }
                }
                return previewText;
            }
        },
        methods: {
            cancel: function() {
                console.log("cancel");
                this.cancelling = true;
                const route = { name: 'Automations' };
                if (this.parentAutomation) {
                    route.query = { automation: this.parentAutomation };
                }
                this.$router.push(route);
            },
            sanitizeContent(event) {
                const textarea = event.target;
                const original = textarea.value;
                const selectionStart = textarea.selectionStart;
                const selectionEnd = textarea.selectionEnd;

                // Replace curly apostrophes with straight ones before updating model
                const sanitized = original.replace(/’/g, "'");

                // Only update if the content actually changed
                if (sanitized !== original) {
                // Update the v-model
                this.smsTemplate.content = sanitized;

                // Calculate the cursor offset due to shorter/longer replacement
                const diff = sanitized.length - original.length;

                // Restore cursor position (adjusting for replacement length if needed)
                this.$nextTick(() => {
                    textarea.selectionStart = selectionStart + diff;
                    textarea.selectionEnd = selectionEnd + diff;
                });
                }
            },
            validateContent() {
                console.log("validating SMS content");
                for (let requiredVariable of this.requiredVariables) {
                    let variable = '${' + requiredVariable.name + '}';
                    if (this.smsTemplate.content.indexOf(variable) < 0) {
                        this.error = true;
                        this.infoMessage = "Your SMS is missing the required variable '" + variable + "', which is required in order to send this SMS to guests.";
                        return false;
                    }
                }
                return true;
            },
            saveContent: async function() {
                console.log("saving");
                if (this.validateContent()) {
                    try {
                        const success = await this.$refs.smsTemplateForm.validate();
                        console.log("Validated:" + success);
                        if (!success) {
                            return;
                        }
                        this.updating = true;
                        this.error = false;
                        this.infoMessage = "Saving SMS template";

                        const body = {
                            content: "" + this.smsTemplate.content
                        };

                        const response = await this.$http.put('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/sms-templates/' + this.reference, body);
                        console.log("SMS template updated. Response: " + response);
                        this.infoMessage = "SMS template saved";
                        this.changed = false;
                        setTimeout(() => {
                            this.updating = false;
                        }, 2000);
                    } catch (error) {
                        this.updating = false;
                        this.error = true;
                        if (error.response) {
                            this.infoMessage = error.response.data.message;
                        } else {
                            this.infoMessage = "Sorry, we can't seem to reach our servers! Please try again";
                        }
                    }
                }
            }
        },
        mounted: async function() {
            this.contentInput = this.$refs.content;
            window.scrollTo(0,0);

            await this.fetchVariables();

            if (this.$route.query && this.$route.query.requiredVariables) {
                this.requiredVariables.splice(0, this.requiredVariables.length);
                const requiredVariable = {"name": this.$route.query.requiredVariables, "value": ""};
                this.requiredVariables.push(requiredVariable);
                this.variablesCopy.push(requiredVariable);
            }
            this.sortVariables();

            if (this.$route.query && this.$route.query.parentAutomation) {
                this.parentAutomation = this.$route.query.parentAutomation;
            }

            this.reference = this.$route.params.reference;
            this.$http.get('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/sms-templates/' + this.reference).then(response => {
                this.smsTemplate = response.data;
                this.changed = false;
            }).catch(error => {
                console.log(error)
            });

            this.saveBeforeLeavingFn = async (action) => {
                if (action === "save") {
                    await this.saveContent();
                    this.$router.push({name: this.navigatingToRoute.name });
                    this.showConfirmLoseChangesDialog = false;
                } else {
                    this.showConfirmLoseChangesDialog = false;
                }
            };
            this.$root.$on('save-sms-template', this.saveBeforeLeavingFn);
        },
        beforeDestroy () {
            this.requiredVariables.splice(0, this.requiredVariables.length);
            this.$root.$off('save-sms-template', this.saveBeforeLeavingFn);
        },
        beforeRouteLeave (to, from, next) {
            if (this.changed && !this.cancelling) {
                this.navigatingToRoute = to;
                this.showConfirmLoseChangesDialog = true;
            } else {
                next();
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../styles/layout/forms.scss";
    @import "../styles/components/question_answer";
    @import "../styles/pages/edit-sms";


    // Todo: ::v-deep Deprecated in Vue 3
    .sms-template__preview ::v-deep .sms-template__preview-variable {
        display: inline-block;
        border: 1px dotted $blue-dark;
        background-color: $blue-light;
        padding: 0 3px;
        color: $black;
    }
</style>

